import type { AppearanceSettings as PlatformAppearanceSettings } from "../platform/Platform";

export type AppearanceMode = PlatformAppearanceSettings["mode"];
/** The theme choice Settings and the platform bridge work with. Same values as
 *  `AppearanceMode`; color mode itself lives in Joy's own `CssVarsProvider` storage
 *  (`MODE_STORAGE_KEY`), not in this module's settings. */
export type AppearanceTheme = AppearanceMode;
export type TextSize = "small" | "default" | "large";
export type Density = "default" | "compact";

/** The persisted device preference for the settings Joy doesn't already own. `mode`
 *  named the field the desktop shell slice (`shellStore.appearance`) used before v2;
 *  color mode itself moved to Joy's `CssVarsProvider` (`MODE_STORAGE_KEY`) so it is not
 *  part of this DTO any more. The sidebar is always the labeled rail: a `sidebar` key an
 *  older build saved is ignored. */
export interface AppearanceSettings {
    textSize: TextSize;
    density: Density;
}

/** The `useAppearance()` / `appearanceStore` contract shape. */
export interface Appearance {
    textSize: TextSize;
    density: Density;
}

export const APPEARANCE_STORAGE_KEY = "pp-viewer:appearance";
/** The `modeStorageKey` `AppearanceProvider` passes to Joy's `CssVarsProvider`. Color
 *  mode lives here, in Joy's own storage, not in `APPEARANCE_STORAGE_KEY` above — the
 *  desktop native-menu bridge (`bootstrap.ts`) reads this key directly since it runs
 *  outside React and cannot call `useColorScheme()`. */
export const MODE_STORAGE_KEY = "pp-viewer:mode";
/** The web's Design v2 mode, System by default. Its key before, `:browser-v2`, held Design v2's
 *  old dark default, which Joy writes on a first visit, so a "dark" stored there was never known
 *  to be a choice: `index.html`'s boot script carries a Light or System choice from it once. */
export const WEB_V2_MODE_STORAGE_KEY = `${MODE_STORAGE_KEY}:web-v2`;

export const DEFAULT_APPEARANCE_SETTINGS: AppearanceSettings = {
    textSize: "default",
    density: "default",
};

const TEXT_SIZES: readonly TextSize[] = ["small", "default", "large"];
const DENSITIES: readonly Density[] = ["default", "compact"];
const TEXT_SCALE_BY_SIZE: Record<TextSize, number> = {
    small: 12 / 13,
    default: 1,
    large: 15 / 13,
};

/** Converts untrusted persisted JSON to a complete appearance preference. */
export function parseAppearanceSettings(value: unknown): AppearanceSettings {
    const defaults = DEFAULT_APPEARANCE_SETTINGS;
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return { ...defaults };
    }
    const persisted = value as Record<string, unknown>;
    return {
        textSize: TEXT_SIZES.includes(persisted.textSize as TextSize)
            ? (persisted.textSize as TextSize)
            : defaults.textSize,
        density: DENSITIES.includes(persisted.density as Density)
            ? (persisted.density as Density)
            : defaults.density,
    };
}

export function serializeAppearanceSettings(settings: AppearanceSettings): string {
    return JSON.stringify(parseAppearanceSettings(settings));
}

/** Parses the browser-storage boundary. */
export function parsePersistedAppearanceSettings(raw: string | null): AppearanceSettings {
    if (!raw) return { ...DEFAULT_APPEARANCE_SETTINGS };
    try {
        return parseAppearanceSettings(JSON.parse(raw));
    } catch {
        return { ...DEFAULT_APPEARANCE_SETTINGS };
    }
}

/** Converts a desktop text-size choice to the platform scale. */
export function textSizeToScale(textSize: TextSize): number {
    return TEXT_SCALE_BY_SIZE[textSize];
}

/** Converts a platform scale to the nearest desktop text-size choice. */
export function textScaleToSize(textScale: number): TextSize {
    return TEXT_SIZES.reduce((closest, candidate) =>
        Math.abs(TEXT_SCALE_BY_SIZE[candidate] - textScale) <
        Math.abs(TEXT_SCALE_BY_SIZE[closest] - textScale)
            ? candidate
            : closest,
    );
}

/** `document.documentElement` (or a test double): the two `data-*` attributes this
 *  module still owns. Color mode is Joy's own `data-joy-color-scheme` attribute now. */
export interface AppearanceAttributeTarget {
    setAttribute(name: string, value: string): void;
    removeAttribute(name: string): void;
}

/** Applies the v2 appearance data attributes. Omits an attribute at its default
 *  (default text size, default density). */
export function applyAppearanceAttributes(
    root: AppearanceAttributeTarget,
    settings: AppearanceSettings,
): void {
    if (settings.textSize === "default") root.removeAttribute("data-text-size");
    else root.setAttribute("data-text-size", settings.textSize);
    if (settings.density === "default") root.removeAttribute("data-density");
    else root.setAttribute("data-density", settings.density);
}
